import React, { useState, createContext, useContext } from 'react'
import { apiDelete, apiPost, apiGet, apiPatch } from '~/util/api'
import { useIsMobile, useIsDesktop } from '../../hooks/useMediaQuery'
import { useToast } from '../../lib/ui/toast'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

const PracticeInterviewingContext = createContext()
const queryClient = new QueryClient()

export const usePracticeInterviewing = () => {
  return useContext(PracticeInterviewingContext)
}

const PracticeInterviewProvider = ({
  current_user_practice_interviews,
  current_user_resumes,
  practice_interview_images,
  practice_interview_ai_requests,
  current_user_saved_jobs: savedJobs,
  general_interview_levels: generalInterviewLevels,
  general_interview_categories: generalInterviewCategories,
  current_user,
  eu,
  children,
}) => {
  const isMobile = useIsMobile()
  const isDesktop = useIsDesktop()
  const [practiceInterviews, setPracticeInterviews] = useState(
    current_user_practice_interviews,
  )
  const [practiceInterviewAiRequests, setPracticeInterviewAiRequests] =
    useState(practice_interview_ai_requests)
  const [resumes, setResumes] = useState(current_user_resumes)
  const { toast } = useToast()

  // Grouped UI state for persistence
  const [uiState, setUiState] = useState({
    aiOrGeneral: true,
    showForm: false,
    formIndex: 0,
    questionIndex: 0,
    showInterview: false,
    interviewParams: {
      level: generalInterviewLevels[generalInterviewLevels.length - 1],
      category: generalInterviewCategories.find(
        (category) => category === 'Common',
      ),
      number_of_questions: 1,
    },
    practiceMode: true,
    audioInterview: false,
    aiInterview: true,
    generatingInterview: false,
    showResponses: false,
    activeTab: 'howDoesPracticeInterviewing',
    disableContinue: true,
    documentId: 0,
  })

  const deletePracticeInterview = async (interview) => {
    try {
      await apiDelete(`/interview/practice_interviewing/${interview?.id}`)
      setPracticeInterviews((prev) => prev.filter((ele) => ele !== interview))
    } catch (error) {
      console.error(error)
    }
    return
  }

  const generatePracticeInterview = async () => {
    setUiState((prev) => ({
      ...prev,
      showForm: false,
      questionIndex: 0,
      audioInterview: uiState.aiInterview ? prev.audioInterview : false,
      practiceMode: uiState.aiInterview ? prev.practiceMode : true,
      generatingInterview: true,
    }))

    let response
    try {
      response = await apiPost(
        `/interview/practice_interviewing/`,
        {
          number_of_questions:
            uiState.interviewParams['number_of_questions'] || 1,
          job_title: uiState.interviewParams['job_title'],
          job_description: uiState.interviewParams['job_description'],
          company: uiState.interviewParams['company'],
          industry: uiState.interviewParams['industry'],
          resume_id: uiState.interviewParams['resume_id'],
          resume_file: uiState.interviewParams['resume_file'],
          practice_mode: uiState.practiceMode,
          audio_interview: uiState.audioInterview,
          ai_interview: uiState.aiInterview,
          category: uiState.interviewParams['category'],
          level: uiState.interviewParams['level'],
        },
        true,
      )
      setUiState((prev) => ({
        ...prev,
        generatingInterview: false,
        showInterview: true,
        documentId: response.data.document_id,
      }))
      toast({ description: 'Practice interview generated successfully' })
    } catch (error) {
      console.error(error)
      setUiState((prev) => ({
        ...prev,
        generatingInterview: false,
        showForm: true,
      }))
      toast({
        description: 'Error: Interview generation failed.',
        variant: 'destructive',
      })
    }
  }

  const getInterviewResponses = async (id) => {
    let response
    try {
      response = await apiGet(
        `/interview/practice_interviewing/${id}/finished_responses/`,
      )
      setPracticeInterviews(response.data.practice_interviews)
      setPracticeInterviewAiRequests(response.data.ai_requests)
    } catch (error) {
      console.error(error)
    }
  }

  const saveInterviewResponse = async (document_id) => {
    const maxIndex = uiState.interviewParams['number_of_questions'] - 1
    await getInterviewResponses(document_id)
    if (uiState.questionIndex < maxIndex) {
      setUiState((prev) => ({ ...prev, questionIndex: prev.questionIndex + 1 }))
    } else {
      setUiState((prev) => ({
        ...prev,
        showResponses: true,
        showInterview: false,
        questionIndex: 0,
      }))
      setTimeout(
        () =>
          queryClient.invalidateQueries([
            'interviewResponses',
            uiState.documentId,
          ]),
        5000,
      )
    }
  }

  const updateInterview = async (interview, interviewName) => {
    let response
    try {
      response = await apiPatch(
        `/interview/practice_interviewing/${interview.id}`,
        { name: interviewName },
      )
      setPracticeInterviews(response.data.practice_interviews)
      setPracticeInterviewAiRequests(response.data.ai_requests)
    } catch (error) {
      console.error(error)
    }
  }

  // Expose grouped state and setters for each key
  const value = {
    practiceInterviews,
    setPracticeInterviews,
    resumes,
    setResumes,
    practiceInterviewAiRequests,
    setPracticeInterviewAiRequests,
    practice_interview_images,
    savedJobs,
    deletePracticeInterview,
    generatePracticeInterview,
    saveInterviewResponse,
    updateInterview,
    generalInterviewLevels,
    generalInterviewCategories,
    current_user,
    eu,
    isMobile,
    isDesktop,
    getInterviewResponses,
    // UI state and setters
    uiState,
    setUiState,
  }
  return (
    <QueryClientProvider client={queryClient}>
      <PracticeInterviewingContext.Provider value={value}>
        {children}
      </PracticeInterviewingContext.Provider>
    </QueryClientProvider>
  )
}

export default PracticeInterviewProvider
